import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { cn } from "@cieda/djayma-ui";
import { Bell } from "lucide-react";
import { useUnreadCount, useMarkAsRead, useMarkAllAsRead } from "@/hooks/admin/useNotifications";
import { notificationsApi } from "@/lib/admin-api/notifications";
import type { AdminNotification } from "@/lib/admin-api/notifications";
import { useQuery } from "@tanstack/react-query";
import { NotificationList } from "./NotificationList";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DROPDOWN_LIMIT = 5;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch unread count
  const { data: unreadCount = 0 } = useUnreadCount();

  // Fetch recent notifications for the dropdown (only when open)
  const { data: recentData } = useQuery({
    queryKey: ["admin", "notifications", "recent"],
    queryFn: () => notificationsApi.list({ limit: DROPDOWN_LIMIT }),
    enabled: open,
    staleTime: 10_000,
  });

  const recent: readonly AdminNotification[] = recentData?.notifications ?? [];

  // Mutations
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const handleMarkRead = useCallback(
    (id: string) => {
      markAsRead.mutate(id);
    },
    [markAsRead],
  );

  const handleMarkAllRead = useCallback(() => {
    markAllAsRead.mutate();
    setOpen(false);
  }, [markAllAsRead]);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Bell trigger */}
      <button
        type="button"
        onClick={toggleOpen}
        className={cn(
          "relative flex h-9 w-9 items-center justify-center rounded-md transition-colors",
          "text-stone-500 hover:bg-stone-100 hover:text-stone-700",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300",
          open && "bg-stone-100 text-stone-700",
        )}
        aria-label="Notifications"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell className="h-5 w-5" />

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span
            className={cn(
              "absolute -right-0.5 -top-0.5 flex items-center justify-center rounded-full",
              "bg-red-500 text-white text-[10px] font-bold leading-none",
              unreadCount > 9 ? "h-4.5 w-4.5 px-0.5" : "h-4 w-4",
            )}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            "absolute right-0 top-full z-50 mt-2 w-80 sm:w-96",
            "rounded-lg border border-stone-200 bg-white shadow-lg",
            "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
          )}
          role="dialog"
          aria-label="Notifications récentes"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-stone-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-stone-900">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          {/* List */}
          <NotificationList
            notifications={recent}
            onMarkRead={handleMarkRead}
            compact
          />

          {/* Footer */}
          <div className="border-t border-stone-100 px-4 py-2.5">
            <Link
              to="/admin/notifications"
              onClick={() => setOpen(false)}
              className="block text-center text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
            >
              Voir toutes les notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
